import React, { useEffect, useState, useCallback, useMemo } from 'react';
import MainCard from '../../ui-component/cards/MainCard';
import HomePageService from "../../services/HomePage";
import GPSHistoryMap from "./GPSHistoryMap";
import GoogleMapComponent from "./HistoryGoogleMapComponent";
import OSMHistoryMap from "./OSMHistoryMap";
import { dateTimeUpdate } from "../../helper";
import {
  FormControl, Autocomplete, TextField, Button, Grid, Typography,
  Box, CircularProgress, Collapse, IconButton, Tooltip
} from '@mui/material';
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTranslation } from "react-i18next";
import AzureHistoryMap from "./AzureHistoryMap";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
//const MAX_HISTORY_RANGE_MS = 1000 * 60 * 60 * 24 * 365 * 2;
const MAX_HISTORY_RANGE_MS = 1000 * 60 * 60 * 48; // 48 Hours

const TOKEN = {
  bg: '#f0f2f5',          // page background
  card: '#ffffff',
  cardBorder: '#e2e5eb',
  accent: '#1e3a5f',      // deep navy  (matches header)
  accentLight: '#2563eb', // bright blue for CTA / icons
  accentGlow: 'rgba(37,99,235,0.12)',
  text: '#1a2236',
  textMuted: '#64748b',
  danger: '#ef4444',
  success: '#16a34a',
  radius: 14,
  radiusSm: 8,
  shadow: '0 4px 24px rgba(30,58,95,0.10)',
  shadowHover: '0 8px 32px rgba(30,58,95,0.18)',
};

const compactInputSx = {
  '& .MuiOutlinedInput-root': {
    height: 36,
    fontSize: 13
  }
};

/* ── reusable label/value badge ── */
const InfoBadge = ({ label, value, color }) => (
  <Box sx={{
    display: 'flex', flexDirection: 'column',
    px: 2, py: 1,
    bgcolor: 'rgba(30,58,95,0.06)',
    borderRadius: TOKEN.radiusSm,
    minWidth: 110
  }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: TOKEN.textMuted, letterSpacing: 1, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: color || TOKEN.text, mt: 0.25 }}>
      {value || '—'}
    </Typography>
  </Box>
);

/* ── styled input override helper ── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: TOKEN.radiusSm,
    bgcolor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKEN.accentLight },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKEN.accentLight, borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: TOKEN.accentLight },
};

/* ── map-type pill button ── */
const MapPill = ({ active, label, icon, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 2.5, py: 1,
      borderRadius: 50,
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: 13,
      transition: 'all .2s',
      bgcolor: active ? TOKEN.accentLight : 'transparent',
      color: active ? '#fff' : TOKEN.textMuted,
      boxShadow: active ? `0 4px 12px ${TOKEN.accentGlow}` : 'none',
      userSelect: 'none',
      '&:hover': {
        bgcolor: active ? TOKEN.accentLight : 'rgba(37,99,235,0.08)',
        color: active ? '#fff' : TOKEN.accentLight,
      }
    }}
  >
    <span>{icon}</span> {label}
  </Box>
);

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
const HistoryPlayback = () => {
  const { t } = useTranslation();

  const { currentDateTime, initialFromDate } = useMemo(() => {
    const now = new Date();
    return {
      currentDateTime: dateTimeUpdate(now),
      initialFromDate: dateTimeUpdate(new Date(now.getTime() - 86400000))
    };
  }, []);

  const [vehicleNo, setVehicleNo] = useState('');
  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(currentDateTime);
  const [vehicleList, setVehicleList] = useState([]);
  const [vehicleGpsData, setVehicleGpsData] = useState([]);
  const [downloadStatus, setDownloadStatus] = useState("");
  const [showHistoryMap, setShowHistoryMap] = useState(false);
  const [showVehicleMap, setShowVehicleMap] = useState(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [isLoadingGpsData, setIsLoadingGpsData] = useState(false);
  const [mapType, setMapType] = useState("google");
  const [historyData, setHistoryData] = useState([]);

  const [owner, setOwner] = useState("");
  const [poi, setPoi] = useState("");
  const [roads, setRoads] = useState("");
  const [polygon, setPolygon] = useState("");
  const [activeTab, setActiveTab] = useState("history");
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    speed: 0,
    progress: 0,
    time: null,
    sat: 0,
    gps: 0,
    network: "",
    ignition: 0,
    battery: "",
    internalBattery: "",
    owner: "",
    deviceId: ""
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning"
  });
  
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false
    }));
  };

  /* ─── fetch vehicle list ─── */
  useEffect(() => {
    let isMounted = true;
    const fetchVehicleList = async () => {
      try {
        const response = await HomePageService.getVehicleList();
        if (isMounted && response?.data) setVehicleList(response.data);
      } catch (error) {
        console.error("Error fetching vehicle list:", error);
      } finally {
        if (isMounted) setIsLoadingVehicles(false);
      }
    };
    fetchVehicleList();
    return () => { isMounted = false; };
  }, []);

  /* ─── fetch history data ─── */
  const fetchHistoryData = async () => {
    try {
      const toUTC = (d) => new Date(d).toISOString().slice(0, 19);
      const url = `${process.env.REACT_APP_BASE_URL}api/gps_history_map_data/?` +
        `vehicle_registration_number=${vehicleNo}` +
        `&start_datetime=${toUTC(fromDate)}` +
        `&end_datetime=${toUTC(toDate)}`;
      const res = await fetch(url, {
        headers: { Authorization: `Token ${sessionStorage.getItem("oAuthToken")}` }
      });
      const data = await res.json();

      const validData = (data?.data || []).filter(item => {
        const lat = parseFloat(item.lat);
        const lon = parseFloat(item.lon);
        return lat !== 0 && lon !== 0 && !isNaN(lat) && !isNaN(lon);
      });
      setHistoryData(validData);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (showHistoryMap && vehicleNo) fetchHistoryData();
  }, [showHistoryMap, vehicleNo]);

  /* ─── fetch live GPS for vehicle map ─── */
  useEffect(() => {
    if (!showVehicleMap || vehicleGpsData.length > 0) return;
    let isMounted = true;
    setIsLoadingGpsData(true);
    const fetchVehicleGpsData = async () => {
      try {
        const response = await HomePageService.getLiveTracking_data({});
        if (isMounted && Array.isArray(response?.data?.data))
          setVehicleGpsData(response.data.data);
      } catch (error) {
        console.error("Error fetching vehicle GPS data:", error);
        if (isMounted) setVehicleGpsData([]);
      } finally {
        if (isMounted) setIsLoadingGpsData(false);
      }
    };
    fetchVehicleGpsData();
    return () => { isMounted = false; };
  }, [showVehicleMap, vehicleGpsData.length]);

  // const validateDateRange = useCallback((from, to) => {
  //   if (new Date(to).getTime() - new Date(from).getTime() > MAX_HISTORY_RANGE_MS)
  //     return "Date range cannot exceed 2 years. Please select a shorter range.";
  //   return null;
  // }, []);
  const validateDateRange = useCallback((from, to) => {
  const diff =
    new Date(to).getTime() - new Date(from).getTime();

  if (diff < 0) {
    return "End Date must be greater than Start Date";
  }

  if (diff > MAX_HISTORY_RANGE_MS) {
    return "Date range cannot exceed 48 hours";
  }

  return null;
}, []);

  const checkHistoryData = async () => {
  try {
    const toUTC = (d) =>
      new Date(d).toISOString().slice(0, 19);

    const url =
      `${process.env.REACT_APP_BASE_URL}api/gps_history_map_data/?` +
      `vehicle_registration_number=${vehicleNo}` +
      `&start_datetime=${toUTC(fromDate)}` +
      `&end_datetime=${toUTC(toDate)}`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Token ${sessionStorage.getItem(
          "oAuthToken"
        )}`
      }
    });

    const data = await res.json();

    const validData = (data?.data || []).filter((item) => {
      const lat = parseFloat(item?.lat);
      const lon = parseFloat(item?.lon);

      return (
        !isNaN(lat) &&
        !isNaN(lon) &&
        lat !== 0 &&
        lon !== 0
      );
    });

    return validData;
  } catch (error) {
    console.error(error);
    return [];
  }
};

  // const handleSubmit = useCallback((e) => {
  //   e.preventDefault();
  //   if (!fromDate || !toDate) { alert("Please select both From Date and To Date"); return; }
  //   const error = validateDateRange(fromDate, toDate);
  //   if (error) { alert(error); return; }
  //   if (vehicleNo) {
  //     setShowHistoryMap(true);
  //     setShowVehicleMap(false);
  //   } else {
  //     setShowHistoryMap(false);
  //     setShowVehicleMap(true);
  //   }
  // }, [fromDate, toDate, vehicleNo, validateDateRange]);


    const handleSubmit = useCallback(
  async (e) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      setSnackbar({
        open: true,
        message: "Please select both From Date and To Date",
        severity: "warning"
      });
      return;
    }

    const error = validateDateRange(fromDate, toDate);

    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: "warning"
      });
      return;
    }

    if (!vehicleNo) {
      setSnackbar({
        open: true,
        message: "Please select a vehicle",
        severity: "warning"
      });
      return;
    }

    try {
      const validData = await checkHistoryData();

      if (!validData.length) {
         console.log("NO DATA FOUND");
        setSnackbar({
          open: true,
          message:
            "No data available. Please select different date and time.",
          severity: "warning"
        });

        setShowHistoryMap(false);

        return;
      }

      setHistoryData(validData);

      setSnackbar({
        open: true,
        message: `Playback loaded successfully (${validData.length} records found)`,
        severity: "success"
      });

      setShowHistoryMap(true);
      setShowVehicleMap(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch playback data",
        severity: "error"
      });
    }
  },
  [fromDate, toDate, vehicleNo, validateDateRange]
);

  const handleVehicleSelect = useCallback((selectedVehicleNo) => {
    setVehicleNo(selectedVehicleNo);
    setShowVehicleMap(false);
    setShowHistoryMap(true);
  }, []);

  const handleVehicleNoChange = useCallback((event, newValue) => {
    setVehicleNo(newValue || '');
  }, []);

  // const handleFromDateChange = useCallback((e) => {
  //   const selected = new Date(e.target.value);
  //   if (Number.isNaN(selected.getTime())) return;
  //   if (selected > new Date()) { alert("From Date cannot be in the future"); return; }
  //   setFromDate(dateTimeUpdate(selected));
  // }, []);

  const handleFromDateChange = useCallback((e) => {
    const selectedFrom = new Date(e.target.value);
  
    if (Number.isNaN(selectedFrom.getTime())) return;
  
    if (selectedFrom > new Date()) {
      setSnackbar({
        open: true,
        message: "From Date cannot be in the future",
        severity: "warning"
      });
      return;
    }
  
    setFromDate(dateTimeUpdate(selectedFrom));
  
    // only validate existing end date
    const selectedTo = new Date(toDate);
  
    const diff =
      selectedTo.getTime() - selectedFrom.getTime();
  
    if (diff > 48 * 60 * 60 * 1000) {
      const maxAllowedDate = new Date(
        selectedFrom.getTime() + 48 * 60 * 60 * 1000
      );
  
      setToDate(dateTimeUpdate(maxAllowedDate));
  
      setSnackbar({
        open: true,
        message:
          "Maximum allowed gap between Start Date and End Date is 48 hours",
        severity: "warning"
      });
    }
  }, [toDate]);

  // const handleToDateChange = useCallback((e) => {
  //   const selected = new Date(e.target.value);
  //   if (Number.isNaN(selected.getTime())) return;
  //   if (selected > new Date()) { alert("To Date cannot be in the future"); return; }
  //   setToDate(dateTimeUpdate(selected));
  // }, []);

   const handleToDateChange = useCallback((e) => {
    const selectedTo = new Date(e.target.value);
    const selectedFrom = new Date(fromDate);
  
    if (Number.isNaN(selectedTo.getTime())) return;
  
    if (selectedTo > new Date()) {
      setSnackbar({
        open: true,
        message: "To Date cannot be in the future",
        severity: "warning"
      });
      return;
    }
  
    const diff =
      selectedTo.getTime() - selectedFrom.getTime();
  
    if (diff > 48 * 60 * 60 * 1000) {
      setSnackbar({
        open: true,
        message:
          "Maximum allowed gap between Start Date and End Date is 48 hours",
        severity: "warning"
      });
  
      const maxAllowedDate = new Date(
        selectedFrom.getTime() + 48 * 60 * 60 * 1000
      );
  
      setToDate(dateTimeUpdate(maxAllowedDate));
      return;
    }
  
    setToDate(dateTimeUpdate(selectedTo));
  }, [fromDate]);

  const handleBackToVehicleSelection = useCallback(() => {
    setShowHistoryMap(false);
    setShowVehicleMap(true);
    setVehicleNo('');
  }, []);

  /* ════════════════ RENDER ════════════════ */
  const thStyle = {
    padding: "12px 16px",
    fontWeight: 700,
    color: "#111827",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap"
  };

  const tdStyle = {
    padding: "12px 16px",
    color: "#374151",
    whiteSpace: "nowrap"
  };

  return (
    <Box sx={{
      bgcolor: "#eef2f7",
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>

      {/* HEADER */}
      {!showHistoryMap && (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          background: "#16345c",
          color: "#fff",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)"
        }}>
          <Typography sx={{
            fontSize: 20,
            fontWeight: 700,
            color: "#fff"
          }}>
            History Playback
          </Typography>
        </Box>
      )}
      {/*  COMPACT SEARCH FORM */}
      {!showHistoryMap && (
        <Box sx={{
          px: 2,
          py: 1.5,
          background: "#ffffff",
          borderBottom: "1px solid #dbe3ef",
          boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
          zIndex: 20
        }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1} alignItems="center">

              <Grid item md={3} xs={12}>
                <Autocomplete
                  size="small"
                  value={vehicleNo}
                  onChange={handleVehicleNoChange}
                  options={vehicleList}
                  loading={isLoadingVehicles}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Vehicle" size="small" />
                  )}
                />
              </Grid>

              <Grid item md={3} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  type="datetime-local"
                  value={fromDate}
                  onChange={handleFromDateChange}
                />
              </Grid>

              <Grid item md={3} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  type="datetime-local"
                  value={toDate}
                  onChange={handleToDateChange}
                />
              </Grid>

              <Grid item md={3} xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    height: 42,
                    borderRadius: "10px",
                    bgcolor: "#16345c",
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: 14,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#1d4579",
                      boxShadow: "none"
                    }
                  }}              >
                  Search
                </Button>
              </Grid>

            </Grid>
          </form>
        </Box>
      )}

      {/* MAP AREA (NO SCROLL) */}
      <Box sx={{
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: 0
      }}>

        {/* VEHICLE MAP */}
        {showVehicleMap && (
          <Box sx={{ flex: 1 }}>
            {isLoadingGpsData ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
            ) : (
              <GoogleMapComponent
                gpsData={vehicleGpsData}
                onMarkerClick={(entryData) => {
                  const vehicleRegNo =
                    entryData.vehicle_registration_number ||
                    entryData.vehicle_reg_no ||
                    entryData.device_tag_info?.device?.vehicle_reg_no;
                  if (vehicleRegNo) handleVehicleSelect(vehicleRegNo);
                }}
              />
            )}
          </Box>
        )}

        {/* HISTORY MAP */}
        {showHistoryMap && vehicleNo && (
          <Grid container spacing={2}>

            {/* LEFT SIDE : MAP (8 COLUMN) */}
            <Grid item xs={12} md={8}>

              <Box
                sx={{
                  borderRadius: "16px",
                  background: "#fff",
                  boxShadow: "0 4px 20px rgba(15,23,42,0.08)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "visible"

                }}
              >

                {/* HEADER */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    background:
                      "linear-gradient(90deg,#16345c,#1f4f88)"
                  }}
                >

                  {/* LEFT */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      color: "#fff"
                    }}
                  >

                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 300
                      }}
                    >History PlayBack -
                      {vehicleNo}
                    </Typography>

                    <span
                      style={{
                        color:
                          stats.speed > 0
                            ? "#22c55e"
                            : "#ef4444"
                      }}
                    >
                      ⚡ {stats.speed} km/h
                    </span>

                    <span>
                      ⏱ {stats.progress}%
                    </span>

                  </Box>

                  {/* RIGHT */}
                  <Box sx={{ display: "flex", gap: 1 }}>

                    <TextField
                      select
                      size="small"
                      value={mapType}
                      onChange={(e) =>
                        setMapType(e.target.value)
                      }
                      SelectProps={{ native: true }}
                      sx={{
                        bgcolor: "#fff",
                        width: 120
                      }}
                    >
                      <option value="google">
                        Google
                      </option>

                      <option value="osm">
                        Open Street
                      </option>

                      <option value="azure">
                        Azure Map
                      </option>

                    </TextField>

                    <Button
                      onClick={
                        handleBackToVehicleSelection
                      }
                      size="small"
                      sx={{
                        bgcolor: "#fff",
                        fontSize: 12
                      }}
                    >
                      ⇄ Select Different Vehicle
                    </Button>

                  </Box>

                </Box>

                {/* MAP */}
                <Box
                  sx={{
                    minHeight: 400,
                    width: "100%",
                    position: "relative",
                    overflow: "hidden",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column"
                  }}
                >
                  {/* FLOATING SEARCH BAR */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      left: 16,
                      zIndex: 100,
                      background: "rgba(255,255,255,0.96)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "14px",
                      px: 2,
                      py: 1.5,
                      boxShadow: "0 8px 24px rgba(15,23,42,0.18)",
                      border: "1px solid rgba(255,255,255,0.6)"
                    }}
                  >
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={1.5} alignItems="center">

                        <Grid item>
                          <Autocomplete
                            size="small"
                            value={vehicleNo}
                            onChange={handleVehicleNoChange}
                            options={vehicleList}
                            loading={isLoadingVehicles}
                            sx={{
                              width: 240,
                              "& .MuiOutlinedInput-root": {
                                height: 46,
                                borderRadius: "10px",
                                background: "#fff"
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Vehicle"
                              />
                            )}
                          />
                        </Grid>

                        <Grid item>
                          <TextField
                            size="small"
                            type="datetime-local"
                  value={fromDate}
                            onChange={handleFromDateChange}
                            sx={{
                              width: 170,
                              "& .MuiOutlinedInput-root": {
                                height: 46,
                                borderRadius: "10px",
                                background: "#fff"
                              }
                            }}
                          />
                        </Grid>

                        <Grid item>
                          <TextField
                            size="small"
                            type="datetime-local"
                  value={fromDate}
                            onChange={handleToDateChange}
                            sx={{
                              width: 170,
                              "& .MuiOutlinedInput-root": {
                                height: 46,
                                borderRadius: "10px",
                                background: "#fff"
                              }
                            }}
                          />
                        </Grid>

                        <Grid item>
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{
                              height: 46,
                              px: 3,
                              borderRadius: "10px",
                              bgcolor: "#16345c",
                              fontWeight: 700,
                              textTransform: "none",
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: "#1d4579",
                                boxShadow: "none"
                              }
                            }}
                          >
                            Show Playback
                          </Button>
                        </Grid>

                      </Grid>
                    </form>
                  </Box>
                  {mapType === "google" ? (
                    <GPSHistoryMap
                      startDateTime={fromDate}
                      endDateTime={toDate}
                      vehicleRegistrationNumber={
                        vehicleNo
                      }
                      poi={poi}
                      owner={owner}
                      roads={roads}
                      polygon={polygon}
                      onStatsUpdate={setStats}
                    />
                  ) : mapType === "osm" ? (
                    <OSMHistoryMap
                      mapData={historyData}
                      onStatsUpdate={setStats}
                    />
                  ) : (
                    <AzureHistoryMap
                      mapData={historyData}
                      onStatsUpdate={setStats}
                    />
                  )}

                </Box>
                <Box
                  sx={{
                    mt: 3.5,
                    background: "#fff",
                    position: "relative",
                    zIndex: 1,
                    borderRadius: "0 0 10px 10px",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column",
                    height: 350,
                    overflow: "hidden"
                  }}
                >
                  {/* HEADER */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      borderBottom: "1px solid #e5e7eb",
                      background: "#fafafa"
                    }}
                  >
                    <Box
                      onClick={() => setActiveTab("history")}
                      sx={{
                        px: 3,
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: 15,
                        cursor: "pointer",
                        borderBottom:
                          activeTab === "history"
                            ? "2px solid #1e3a5f"
                            : "2px solid transparent",
                        color:
                          activeTab === "history"
                            ? "#111827"
                            : "#9ca3af"
                      }}
                    >
                      History
                    </Box>

                    <Box
                      onClick={() => setActiveTab("coordinates")}
                      sx={{
                        px: 3,
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: 15,
                        cursor: "pointer",
                        borderBottom:
                          activeTab === "coordinates"
                            ? "2px solid #1e3a5f"
                            : "2px solid transparent",
                        color:
                          activeTab === "coordinates"
                            ? "#111827"
                            : "#9ca3af"
                      }}
                    >
                      Coordinates
                    </Box>
                  </Box>

                  {/* TABLE */}
                  <Box sx={{
                    overflowX: "auto",
                    overflowY: "auto",
                    flex: 1,
                  }}>
                    {activeTab === "history" ? (
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 14
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              background: "#f8fafc",
                              textAlign: "left"
                            }}
                          >
                            <th style={thStyle}>Time</th>
                            <th style={thStyle}>Coordinate</th>
                            <th style={thStyle}>Speed</th>
                            <th style={thStyle}>Heading</th>
                            <th style={thStyle}>Event</th>
                          </tr>
                        </thead>

                        <tbody>
                          {historyData.map((item, index) => {
                            const speed = Number(item?.s || 0);

                            let eventText = "Moving";

                            if (speed === 0) {
                              eventText = "Stop";
                            }

                            if (item?.dos === "10") {
                              eventText = "Door Open";
                            }

                            return (
                              <tr
                                key={index}
                                style={{
                                  borderBottom: "1px solid #f1f5f9"
                                }}
                              >
                                <td style={tdStyle}>
                                  {new Date(item.et).toLocaleString()}
                                </td>

                                <td style={tdStyle}>
                                  {Number(item.lat).toFixed(6)},
                                  {" "}
                                  {Number(item.lon).toFixed(6)}
                                </td>

                                <td style={tdStyle}>
                                  {speed.toFixed(1)} km/h
                                </td>

                                <td style={tdStyle}>
                                  {Number(item.h || 0).toFixed(1)}°
                                </td>

                                <td style={tdStyle}>
                                  {eventText}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 14
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              background: "#f8fafc",
                              textAlign: "left"
                            }}
                          >
                            <th style={thStyle}>Latitude</th>
                            <th style={thStyle}>Longitude</th>
                          </tr>
                        </thead>

                        <tbody>
                          {historyData.map((item, index) => (
                            <tr
                              key={index}
                              style={{
                                borderBottom: "1px solid #f1f5f9"
                              }}
                            >
                              <td style={tdStyle}>
                                {Number(item.lat).toFixed(6)}
                              </td>

                              <td style={tdStyle}>
                                {Number(item.lon).toFixed(6)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </Box>
                </Box>


              </Box>

            </Grid>

            {/* RIGHT SIDE : EVENTS + STATS (4 COLUMN) */}
            <Grid item xs={12} md={4}>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  height: "100%"
                }}
              >

                {/* HISTORICAL EVENTS */}
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: "16px",
                    border: "1px solid #e5e7eb",
                    overflow: "hidden",
                    boxShadow:
                      "0 4px 20px rgba(15,23,42,0.08)"
                  }}
                >

                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: 18,
                      borderBottom:
                        "1px solid #e5e7eb",
                      background: "#f8fafc"
                    }}
                  >
                    Historical Events
                  </Box>

                  <Box sx={{ p: 2 }}>

                    {historyData
                      .slice(0, 8)
                      .map((item, index) => {

                        const speed = Number(
                          item?.s || 0
                        );

                        let event = "Moving";
                        let color = "#22c55e";
                        let icon = "🟢";

                        if (speed === 0) {
                          event = "Stop";
                          color = "#ef4444";
                          icon = "🔴";
                        }

                        if (item?.dos === "10") {
                          event = "Door Open";
                          color = "#16a34a";
                          icon = "🚪";
                        }

                        return (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              justifyContent:
                                "space-between",
                              alignItems: "center",
                              py: 1.2,
                              borderBottom:
                                index !== 7
                                  ? "1px solid #f1f5f9"
                                  : "none"
                            }}
                          >

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1
                              }}
                            >
                              <Typography>
                                {icon}
                              </Typography>

                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color
                                }}
                              >
                                {event}
                              </Typography>
                            </Box>

                            <Typography
                              sx={{
                                fontSize: 13,
                                color: "#6b7280"
                              }}
                            >
                              {new Date(
                                item.et
                              ).toLocaleTimeString()}
                            </Typography>

                          </Box>
                        );
                      })}

                  </Box>

                </Box>

                {/* SUMMARY */}
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: "16px",
                    border: "1px solid #e5e7eb",
                    overflow: "hidden",
                    boxShadow:
                      "0 4px 20px rgba(15,23,42,0.08)"
                  }}
                >

                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: 18,
                      borderBottom:
                        "1px solid #e5e7eb",
                      background: "#f8fafc"
                    }}
                  >
                    Summary Statistics
                  </Box>

                  <Box sx={{ p: 2 }}>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        py: 1
                      }}
                    >
                      <Typography>
                        Total Distance
                      </Typography>

                      <Typography fontWeight={700}>
                        {
                          Number(
                            historyData[
                              historyData.length - 1
                            ]?.om || 0
                          ).toFixed(1)
                        } km
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        py: 1
                      }}
                    >
                      <Typography>
                        Max Speed
                      </Typography>

                      <Typography fontWeight={700}>
                        {
                          Math.max(
                            ...historyData.map(
                              (i) =>
                                Number(i.s || 0)
                            )
                          ).toFixed(1)
                        } km/h
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        py: 1
                      }}
                    >
                      <Typography>
                        Average Speed
                      </Typography>

                      <Typography fontWeight={700}>
                        {
                          (
                            historyData.reduce(
                              (a, b) =>
                                a +
                                Number(
                                  b.s || 0
                                ),
                              0
                            ) / historyData.length
                          ).toFixed(1)
                        } km/h
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        py: 1
                      }}
                    >
                      <Typography>
                        Satellites
                      </Typography>

                      <Typography fontWeight={700}>
                        {
                          historyData[0]?.sat || 0
                        }
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        py: 1
                      }}
                    >
                      <Typography>
                        Battery
                      </Typography>

                      <Typography fontWeight={700}>
                        {
                          historyData[0]?.miv || 0
                        }{" "}
                        V
                      </Typography>
                    </Box>

                  </Box>

                </Box>

              </Box>

            </Grid>

          </Grid>
        )}

      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        sx={{
          zIndex: 9999999,
          position: "fixed",
          top: "80px !important"
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            minWidth: 400
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default React.memo(HistoryPlayback);
